/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
import React, { useCallback, useEffect, useRef } from 'react';
import { isDesktop, isChrome } from 'react-device-detect';
import {
  isGpuSessionCapReached,
  resolveGpuSessionDeadline,
  resolveGpuSessionWarnings,
  showInitMessage,
} from './helpers';
import {
  IGameCast,
  IGameCastArgs,
  IGamecastServerDisconnectReason,
  IGamecastVideoStats,
} from './interfaces';
import GameCastService from '@services/GameCastService';
import { recordExperienceContext } from '@services/reportIt/experienceDiagnostics';
import {
  GAME_CAST_REQUEST_TIMEOUT,
  enableDebug,
  disallowKeyCode,
} from './constants';
import { IInitMessage } from '@interfaces/streaming';
import { IParticipant } from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import { LeaveEventReason } from '@providers/MytaverseProvider/interfaces';
import { useChatState } from '@core/chat/context';
import { RegisterTouchToMouse } from './touchtomouse';
import { IStartStreamSessionOutput } from '@interfaces/gamecast';
import {
  reportStreamConnectionState,
  reportUEGameliftCrash,
} from '@helpers/sentry';
import { recordStreamConnectionState } from '@services/reportIt/connectionStateStore';
import {
  startRtcStatsSampling,
  stopRtcStatsSampling,
} from '@services/reportIt/rtcStatsBuffer';

import { default as gameliftStreamsLogger } from './logger';

import { NOTIFICATION_TYPES } from '@components/Notification/constants';
import { env } from 'env';
import { formatString } from '@helpers/string';

const {
  REACT_APP_GAMELIFT_STREAMS_CONNECTION_TIMEOUT_SECONDS = 5 * 60,
  REACT_APP_GAMELIFT_STREAMS_SESSION_STATUS_CHECK_TIMEOUT_SECONDS = 15,
} = env;
const WEBRTC_STATS_MIN_SAMPLES = 450;

export const useGameInitialization = ({
  streamId,
  setIsGamecastCrash,
  setIsGamecastTimeout,
  gamecastStats,
  currentRoom,
  disabledKeyCommands,
  externalKeyboardConnected,
  setShowExternalKeyboardOverlay,
  onSessionDurationWarning,
  onSessionDurationLimitReached,
}: {
  browserType: string;
  streamId: string | null;
  currentParticipant: IParticipant | undefined;
  setStreamingProvider: React.Dispatch<
    React.SetStateAction<StreamingProviders | null>
  >;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  setIsGamecastTimeout: React.Dispatch<React.SetStateAction<boolean>>;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  currentRoom: IRoom | null | undefined;
  disabledKeyCommands: string[];
  externalKeyboardConnected: boolean;
  setShowExternalKeyboardOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  onSessionDurationWarning: (messageKey: string) => void;
  onSessionDurationLimitReached: () => void;
}) => {
  const { sendMessageToUnreal, navigateToEventsPage, user } = useMytaverse();
  const { setOpen: setOpenChat } = useChatState();

  const disabledKeyCommandsRef = useRef<string[]>(disabledKeyCommands);
  disabledKeyCommandsRef.current = disabledKeyCommands;

  const gamecastClientRef = useRef<null | IGameCast>(null);
  const gamecastClientConnectedRef = useRef(false);
  const gamecastRTCStatsIntervalRef = useRef<any>();
  const streamStartedConnectionRef = useRef<boolean>(false);
  const abortConnectionRef = useRef<boolean>(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const connectionAttemptIdRef = useRef(0);
  const gamecastConnectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(
    null,
  );
  const gameliftStreamsSessionStatusTimeoutRef = useRef<NodeJS.Timeout | null>(
    null,
  );

  // Wall-clock instant AWS will terminate the session at, or null when the
  // project sets no cap. Used to tell a configured cutoff from a real crash.
  const gpuSessionDeadlineRef = useRef<number | null>(null);
  const gpuSessionWarningTimeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Store session context for crash reporting
  const sessionContextRef = useRef<{
    sessionArn?: string;
    applicationArn?: string;
    userId?: string;
    streamGroupId?: string;
    region?: string;
  }>({});

  //@ts-ignore
  const GamecastClient = window.gameliftstreams.GameLiftStreams;
  window.gameliftstreams.setLogLevel('debug');

  let connectedTime = 0;

  if (enableDebug) {
    //@ts-ignore
    window.gameliftstreams.setLogLevel('debug');
  }

  const closeGamecastStream = () => {
    abortConnectionRef.current = true;
    gpuSessionDeadlineRef.current = null;

    gpuSessionWarningTimeoutsRef.current.forEach(clearTimeout);
    gpuSessionWarningTimeoutsRef.current = [];

    if (gamecastClientRef.current) {
      gameliftStreamsLogger.log(`Closing gamelift stream session`);
      gamecastClientRef.current.close();
      gamecastClientRef.current = null;
    }

    if (gamecastRTCStatsIntervalRef.current) {
      clearInterval(gamecastRTCStatsIntervalRef.current);
      gamecastRTCStatsIntervalRef.current = null;
    }

    // MV-1077 — stop the Report It rolling RTC window with the stream. The
    // buffered samples are retained so a report filed right after the stream
    // ends ("it froze, so I quit") still carries the recent window.
    stopRtcStatsSampling();
  };

  const abortGamecastConnection = () => {
    if (!streamStartedConnectionRef.current) {
      gameliftStreamsLogger.log(`Aborting gamelift streams connection`);
    }
    abortConnectionRef.current = true;

    closeGamecastStream();
  };

  const handleGamecastConnectionTimeout = useCallback(() => {
    gameliftStreamsLogger.log('Gamelift Streams connection timeout.');

    setIsGamecastTimeout(true);
  }, [setIsGamecastTimeout]);

  const clearGamecastConnectionTimeout = useCallback(() => {
    if (gamecastConnectionTimeoutRef.current !== null) {
      gameliftStreamsLogger.log(
        'Clear Gamelift Streams Session connection timeout',
      );
      clearTimeout(gamecastConnectionTimeoutRef.current);
      gamecastConnectionTimeoutRef.current = null;
    }
  }, []);

  const initializeGamecastConnectionTimeout = useCallback(() => {
    clearGamecastConnectionTimeout();

    gameliftStreamsLogger.log(
      `Start gamelift streams connection timeout (${REACT_APP_GAMELIFT_STREAMS_CONNECTION_TIMEOUT_SECONDS} seconds)`,
    );

    gamecastConnectionTimeoutRef.current = setTimeout(
      handleGamecastConnectionTimeout,
      Number(REACT_APP_GAMELIFT_STREAMS_CONNECTION_TIMEOUT_SECONDS) * 1000,
    );
  }, [handleGamecastConnectionTimeout, clearGamecastConnectionTimeout]);

  const handleGameliftStreamsSessionStatusTimeout = useCallback(() => {
    // Must name the same constant that arms this handler in
    // initGameliftStreamsSessionStatusTimeout below (the 15 s status watchdog),
    // not the 5-minute overall connection timeout — they are easy to confuse and
    // the wrong one makes log triage read a 15 s giveup as a 5-minute wait.
    gameliftStreamsLogger.log(
      `No WS messages about gamelift streams session status for the last ${REACT_APP_GAMELIFT_STREAMS_SESSION_STATUS_CHECK_TIMEOUT_SECONDS} seconds`,
    );

    setIsGamecastTimeout(true);
  }, [setIsGamecastTimeout]);

  const clearGameliftStreamsSessionStatusTimeout = useCallback(() => {
    if (gameliftStreamsSessionStatusTimeoutRef.current) {
      clearTimeout(gameliftStreamsSessionStatusTimeoutRef.current);
      gameliftStreamsSessionStatusTimeoutRef.current = null;
    }
  }, []);

  const initGameliftStreamsSessionStatusTimeout = useCallback(() => {
    clearGameliftStreamsSessionStatusTimeout();

    gameliftStreamsSessionStatusTimeoutRef.current = setTimeout(
      handleGameliftStreamsSessionStatusTimeout,
      Number(REACT_APP_GAMELIFT_STREAMS_SESSION_STATUS_CHECK_TIMEOUT_SECONDS) *
        1000,
    );
  }, [
    setIsGamecastTimeout,
    clearGameliftStreamsSessionStatusTimeout,
    handleGameliftStreamsSessionStatusTimeout,
  ]);

  const getGameCastClient = (
    videoElement: HTMLVideoElement,
    isReconnect?: boolean,
  ) => {
    const audioElement = document.getElementById(
      'gamecastAudioTag',
    ) as HTMLAudioElement | null;

    if (!audioElement) {
      throw new Error(
        'GameCast audio element (#gamecastAudioTag) not found in DOM',
      );
    }

    const gamecastClient: IGameCast = new GamecastClient({
      videoElement,
      audioElement,
      inputConfiguration: {
        autoMouse: true,
        autoKeyboard: true,
        autoGamepad: true,
        resetOnDetach: true,
        hapticFeedback: true,
        setCursor: 'visibility',
        autoPointerLock: true,
        mouseFilter: () => true,
        keyboardFilter: (event) => {
          if (enableDebug) {
            console.warn('gamecast keyboard event', { event });
          }

          const disabledKeys = disabledKeyCommandsRef.current.map(
            (key) => `Key${key.toUpperCase()}`,
          );

          if (
            disallowKeyCode.indexOf(event.code) > -1 ||
            disabledKeys.indexOf(event.code) > -1
          ) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          if (event.metaKey) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          if (event.key === 'F11') {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          return true;
        },
      },
      clientConnection: {
        connectionState: async (state) => {
          gameliftStreamsLogger.log(`Connection state: ${state}`);

          // MV-1077 — connection-state telemetry. The Report It snapshot reads
          // the transition history (silent disconnect→reconnect cycles are the
          // leading cause of "freezes every few seconds" reports), and Sentry
          // gets a breadcrumb per transition + a warning event on disconnect.
          recordStreamConnectionState(state);
          reportStreamConnectionState(state, {
            sessionArn: sessionContextRef.current.sessionArn,
            streamGroupId: sessionContextRef.current.streamGroupId,
          });

          switch (state) {
            case 'connected':
              connectedTime = Date.now();
              break;
            case 'disconnected':
              if (
                gamecastClientRef.current &&
                sessionContextRef.current?.sessionArn &&
                sessionContextRef.current?.streamGroupId
              ) {
                console.log(
                  `Reconnect to current stream session ${sessionContextRef.current.sessionArn}`,
                );

                try {
                  const signalRequest =
                    await gamecastClientRef.current.generateSignalRequest();

                  const sessionId = sessionContextRef.current.sessionArn
                    .split('/')
                    .pop();
                  const streamGroupId = sessionContextRef.current.streamGroupId;

                  if (sessionId) {
                    const connectToStreamSessionResponse =
                      await GameCastService.connectToStreamSession(sessionId, {
                        streamGroupId,
                        signalRequest,
                      });

                    if (connectToStreamSessionResponse) {
                      await gamecastClientRef.current.processSignalResponse(
                        connectToStreamSessionResponse.signalResponse,
                      );
                    }
                  }
                } catch (e) {
                  gameliftStreamsLogger.log(
                    `Error reconnection to curren stream session ${sessionContextRef.current.sessionArn}`,
                    e,
                  );
                }
              }

              break;
            case 'failed':
              reportUEGameliftCrash({
                sessionArn: sessionContextRef.current.sessionArn,
                region: sessionContextRef.current.region,
                disconnectReason: `connection_state: ${state}`,
                userId: sessionContextRef.current.userId,
                streamId: streamId || undefined,
              });

              setIsGamecastCrash(true);
              break;
            default:
              break;
          }
        },
        channelError: (state) => {
          gameliftStreamsLogger.log(`Channel error: ${state}`);

          if (state) {
            reportUEGameliftCrash({
              sessionArn: sessionContextRef.current.sessionArn,
              region: sessionContextRef.current.region,
              disconnectReason: `channel_error: ${state}`,
              userId: sessionContextRef.current.userId,
              streamId: streamId || undefined,
            });
          }
        },
        serverDisconnect: async (state: IGamecastServerDisconnectReason) => {
          gameliftStreamsLogger.log(`Server Disconnect: ${state}`);

          switch (state) {
            case IGamecastServerDisconnectReason.Terminated: {
              // The project's max GPU session duration elapsed. Reconnecting
              // would start a fresh session with a fresh cap, and a successful
              // start resets the reconnect budget — so it would loop forever.
              // End the experience instead, and keep it out of crash reporting.
              if (isGpuSessionCapReached(gpuSessionDeadlineRef.current)) {
                gameliftStreamsLogger.log(
                  'Max GPU session duration reached — ending experience',
                );

                closeGamecastStream();
                onSessionDurationLimitReached();

                navigateToEventsPage({
                  reason: LeaveEventReason.GpuSessionLimitReached,
                  message: 'notifications.gpuSessionDurationReached',
                  type: NOTIFICATION_TYPES.INFO,
                  recordFailure: false,
                });

                break;
              }

              reportUEGameliftCrash({
                sessionArn: sessionContextRef.current.sessionArn,
                region: sessionContextRef.current.region,
                disconnectReason: state,
                userId: sessionContextRef.current.userId,
                streamId: streamId || undefined,
              });

              if (gamecastClientRef.current) {
                gamecastClientRef.current.close();
              }
              setIsGamecastCrash(true);

              if (isReconnect) {
                //setOpenChat(false);
              }
              break;
            }
            default:
              break;
          }
        },
        applicationMessage: (state) => {
          try {
            const message: any = JSON.parse(new TextDecoder().decode(state));

            gameliftStreamsLogger.log(`Application message: ${message}`);

            if (message.action === 'CLOSE_TAB') {
              window.close();
            }
          } catch (e) {
            console.error(`Error handling application message: ${e}`);
          }
        },
      },
    } as IGameCastArgs);

    return gamecastClient;
  };

  const processActiveStreamSession = async (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => {
    let isStarted = false;

    if (gamecastClientConnectedRef.current) {
      gameliftStreamsLogger.log(
        `Skip processSignalResponse for sessionId: ${gamecastStreamSession.Arn?.split(
          '/',
        ).pop()}. Gamelift steams session already connected`,
      );
      isStarted = true;
    } else if (
      gamecastStreamSession &&
      !abortConnectionRef.current &&
      gamecastClientRef.current
    ) {
      clearGamecastConnectionTimeout();
      gameliftStreamsLogger.log(
        `Start processSignalResponse for sessionId: ${gamecastStreamSession.Arn?.split(
          '/',
        ).pop()}`,
      );

      gamecastClientConnectedRef.current = true;
      await gamecastClientRef.current.processSignalResponse(
        gamecastStreamSession?.SignalResponse as string,
      );
      console.log('[GC Input] attachInput — processActiveStreamSession');
      gamecastClientRef.current.attachInput();
      syncSdkCursorPos();
      isStarted = true;

      sessionContextRef.current = {
        sessionArn: gamecastStreamSession.Arn,
        applicationArn: gamecastStreamSession.ApplicationArn,
        userId: gamecastStreamSession.UserId,
        streamGroupId: gamecastStreamSession.StreamGroupId,
      };

      // AWS starts the session-length clock when the session reaches ACTIVE,
      // which is now — not when it was created, which can be minutes earlier.
      gpuSessionDeadlineRef.current = resolveGpuSessionDeadline(
        gamecastStreamSession.SessionLengthSeconds,
      );

      gpuSessionWarningTimeoutsRef.current = resolveGpuSessionWarnings(
        gamecastStreamSession.SessionLengthSeconds,
      ).map(({ delayMs, messageKey }) =>
        setTimeout(() => onSessionDurationWarning(messageKey), delayMs),
      );

      // MV-1077 — capture the stream-session ARN as soon as it's known. It is
      // only held in this ref (not the event context), so without this a Report
      // It filed after a crash/navigation would lose it. recordExperienceContext
      // merges, so this augments the websocketSessionId/eventId the sync writes.
      recordExperienceContext({
        entities: {
          streamSessionId: gamecastStreamSession.Arn,
          streamGroupId: gamecastStreamSession.StreamGroupId,
        },
        outcome: 'active',
      });

      gameliftStreamsLogger.log(
        `Gamelift streams session ${JSON.stringify(sessionContextRef.current)}`,
      );

      sendMessageToUnreal({
        action: 'CLIENT_CONNECTION_DATA',
        streamingProvider: 'GAMECAST',
        sessionArn: gamecastStreamSession.Arn,
        region: sessionContextRef.current.region,
      });
    }

    if (abortConnectionRef.current) {
      abortGamecastConnection();
      isStarted = false;
    }

    streamStartedConnectionRef.current = isStarted;

    return { isStarted, isAborted: abortConnectionRef.current };
  };

  const startGameliftStreamsStreamSession = useCallback(
    async (
      participantId: string,
      websocketSessionId: string,
      timeout: number,
      streamId: string,
      initMessageHandler: () => Promise<IInitMessage | null>,
      region: string,
      sendMessageToUnreal: (message: any) => void,
      launchArgs: string[] | null,
    ): Promise<void> => {
      const attemptId = connectionAttemptIdRef.current;

      let lastStatTime = 0;
      let lastStatBytesReceived = 0;
      let statSamples: {
        downstreamTransferSpeed: string;
        downstreamConnectionSpeed: number;
        secondsElapsed: number;
      }[] = [];

      const description = 'Gamecast streaming session';
      const gameName = streamId;
      const videoElement = document.getElementById('gamecastVideoTag') as any;

      const gamecast: IGameCast = getGameCastClient(videoElement, true);

      gamecastClientRef.current = gamecast;

      videoElement.addEventListener('mousedown', (e: MouseEvent) => {
        // Edge does not automatically move keyboard focus away from a focused
        // text input when the video element is clicked, so the GameCast SDK
        // never recaptures keyboard input. Blurring the active element here
        // covers <input>, <textarea>, and contenteditable (Stream Chat).
        const active = document.activeElement as HTMLElement | null;

        if (
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active?.isContentEditable
        ) {
          active.blur();
        }

        console.log('[GC Input] RAW mousedown on videoElement', {
          button: e.button,
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
          focused: document.hasFocus(),
          connected: gamecastClientConnectedRef.current,
          t: Date.now(),
        });
      });

      videoElement.addEventListener('mouseenter', (e: MouseEvent) => {
        console.log('[GC Input] mouseenter', {
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
        });
      });

      gamecastRTCStatsIntervalRef.current = setInterval(async () => {
        const stats = await gamecast.getVideoRTCStats();

        for (const id of stats.keys()) {
          const stat = stats.get(id);

          if (stat.type === 'inbound-rtp') {
            // webrtc stats derived speed
            const webrtcStatsSpeed = {
              transferSpeed: 0,
              connectionSpeed: 0,
              avgTransferSpeed: 0,
              avgConnectionSpeed: 0,
              minSamplesCollected: false,
            };
            if (lastStatTime) {
              const mbytesReceived = (
                (stat.bytesReceived - lastStatBytesReceived) /
                Math.pow(1024, 2)
              ).toFixed(2);
              const secondsElapsed = (Date.now() - lastStatTime) / 1000;
              const bitrate = (8 * +mbytesReceived) / secondsElapsed;

              // keep only last WEBRTC_STATS_MIN_SAMPLES stat samples to calculate average speed
              // approx last 15 mins (WEBRTC_STATS_MIN_SAMPLES * 2 seconds)
              if (statSamples.length > WEBRTC_STATS_MIN_SAMPLES) {
                statSamples.shift();
              }
              statSamples.push({
                downstreamTransferSpeed: mbytesReceived,
                downstreamConnectionSpeed: +mbytesReceived * 8,
                secondsElapsed,
              });
              const avgTransferSpeed =
                statSamples.reduce(
                  (acc, cur) => acc + +cur.downstreamTransferSpeed,
                  0,
                ) / statSamples.length;
              const avgBitrate =
                statSamples.reduce(
                  (acc, cur) =>
                    acc +
                    (8 * +cur.downstreamTransferSpeed) / cur.secondsElapsed,
                  0,
                ) / statSamples.length;
              if (enableDebug) {
                console.warn(
                  formatString(
                    `current transfer speed: %sMBs\n server connection speed: %sMBPs\n avg transfer speed: %sMBs over %s samples\n avg server connection speed %s MBPs`,
                    mbytesReceived,
                    bitrate.toFixed(2),
                    avgTransferSpeed.toFixed(2),
                    statSamples.length,
                    avgBitrate.toFixed(2),
                  ),
                  {
                    stat,
                    mbytesReceived,
                    secondsElapsed,
                    bytesrec: stat.bytesReceived,
                    lastStatBytesReceived,
                    bitrate,
                  },
                );
              }
              webrtcStatsSpeed.transferSpeed = +mbytesReceived;
              webrtcStatsSpeed.connectionSpeed = +bitrate.toFixed(2);
              webrtcStatsSpeed.avgTransferSpeed = +avgTransferSpeed.toFixed(2);
              webrtcStatsSpeed.avgConnectionSpeed = +avgBitrate.toFixed(2);
            }
            lastStatTime = stat.timestamp;
            lastStatBytesReceived = stat.bytesReceived;
            // flag to wait 120 seconds before showing speed test check toaster
            // stats are collected every 2 seconds, 60 samples * 2 seconds = 120s
            webrtcStatsSpeed.minSamplesCollected = statSamples.length > 60;

            gamecastStats.current = {
              ...stat,
              ...webrtcStatsSpeed,
            };
          }
        }
      }, 2000);

      // MV-1077 — start the Report It rolling RTC window for the whole session.
      // Merges video + audio inbound-rtp into one report (so both series fill),
      // mirroring useReportItContext's getStats. Idempotent across reconnects.
      startRtcStatsSampling({
        getStats: async (): Promise<RTCStatsReport> => {
          const merged = new Map<string, unknown>();
          const [videoStats, audioStats] = await Promise.all([
            gamecast.getVideoRTCStats?.(),
            gamecast.getAudioRTCStats?.(),
          ]);
          videoStats?.forEach((value: unknown, key: string) =>
            merged.set(key, value),
          );
          audioStats?.forEach((value: unknown, key: string) =>
            merged.set(key, value),
          );
          return merged as unknown as RTCStatsReport;
        },
      });

      RegisterTouchToMouse(videoElement as any);

      const gamecastSignalData = await gamecast.generateSignalRequest();

      gameliftStreamsLogger.log(`Starting new gamelift streams session`);
      gamecastClientConnectedRef.current = false;

      try {
        const initMessage = await initMessageHandler();
        if (!initMessage) {
          return;
        }

        showInitMessage(JSON.stringify(initMessage));

        if (attemptId !== connectionAttemptIdRef.current) {
          gameliftStreamsLogger.log(
            `Skip startGameSession for stale attemptId ${attemptId} (current ${connectionAttemptIdRef.current})`,
          );
          return;
        }

        if (abortConnectionRef.current) {
          return;
        }

        await GameCastService.startGameSession({
          description,
          gameName,
          participantId,
          signalRequest: gamecastSignalData,
          timeout,
          streamId,
          initMessage,
          region,
          websocketSessionId,
          launchArgs,
          sessionKey: initMessage.sessionKey,
        });
      } catch (e) {
        gameliftStreamsLogger.log(`Couldn't start gamelift streams session`);
      }
    },
    [],
  );

  const initializeGamecastStream = useCallback(
    async (
      participantId = '',
      websocketSessionId: string,
      initMessageHandler: () => Promise<IInitMessage | null>,
      region: string,
      launchArgs: string[] | null,
    ) => {
      let isStarted = false;
      if (!streamId) {
        return { isStarted };
      }

      connectionAttemptIdRef.current += 1;

      try {
        await startGameliftStreamsStreamSession(
          participantId,
          websocketSessionId,
          GAME_CAST_REQUEST_TIMEOUT,
          streamId,
          initMessageHandler,
          region,
          sendMessageToUnreal,
          launchArgs,
        );
      } catch (e) {
        gameliftStreamsLogger.error(e);
      }

      return {
        isAborted: abortConnectionRef.current,
        gamecastRTCStatsInterval: gamecastRTCStatsIntervalRef.current,
      };
    },
    [streamId, startGameliftStreamsStreamSession, sendMessageToUnreal],
  );

  const handleReconnectGamecast = useCallback(
    async ({
      participantId,
      websocketSessionId,
      initMessageHandler,
      region,
      launchArgs,
    }: {
      participantId: string;
      websocketSessionId: string;
      initMessageHandler: () => Promise<IInitMessage | null>;
      region: string;
      launchArgs: string[] | null;
    }) => {
      if (!participantId || !streamId) {
        return;
      }

      gameliftStreamsLogger.log(`Reconnect Gamelift Streams`);

      closeGamecastStream();

      abortConnectionRef.current = false;

      initializeGamecastConnectionTimeout();
      initGameliftStreamsSessionStatusTimeout();

      await initializeGamecastStream(
        participantId,
        websocketSessionId,
        initMessageHandler,
        region,
        launchArgs,
      );
    },
    [
      streamId,
      initializeGamecastConnectionTimeout,
      initGameliftStreamsSessionStatusTimeout,
      closeGamecastStream,
    ],
  );

  // Syncs the SDK's internal cursor position to the last globally tracked mouse position.
  // Needed because mousemove events only fire on the video element when the cursor is
  // directly over it (not over higher-z-index React elements), so the SDK loses track
  // of the cursor during normal UI interaction. Without this sync after attachInput(),
  // the SDK sends clicks to the last position it saw — typically the window edge.
  const syncSdkCursorPos = () => {
    const videoElement = document.getElementById(
      'gamecastVideoTag',
    ) as HTMLVideoElement | null;

    if (!videoElement) {
      return;
    }

    const { x, y } = lastMousePosRef.current;

    videoElement.dispatchEvent(
      new MouseEvent('mousemove', {
        bubbles: true,
        cancelable: true,
        clientX: x,
        clientY: y,
        movementX: 0,
        movementY: 0,
      }),
    );
  };

  useEffect(() => {
    const trackMousePos = (e: MouseEvent) => {
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', trackMousePos);

    return () => {
      document.removeEventListener('mousemove', trackMousePos);
    };
  }, []);

  useEffect(() => {
    const handleBlur = () => {
      console.log('[GC Input] window blur — detachInput', {
        activeElement:
          (document.activeElement as HTMLElement)?.id ||
          document.activeElement?.tagName,
        hasClient: !!gamecastClientRef.current,
        hasRoom: !!currentRoom,
      });

      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      if (!isDesktop && externalKeyboardConnected) {
        setShowExternalKeyboardOverlay(true);
      }

      try {
        gamecastClientRef.current?.detachInput();
      } catch (e) {
        console.error(e);
      }
    };

    const handleFocus = () => {
      console.log('[GC Input] window focus — attachInput', {
        hasClient: !!gamecastClientRef.current,
        hasRoom: !!currentRoom,
      });

      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      try {
        gamecastClientRef.current?.attachInput();
        syncSdkCursorPos();
      } catch (e) {
        console.error(e);
      }
    };

    const handleVisibilityChange = () => {
      console.log('[GC Input] visibilitychange', {
        state: document.visibilityState,
        focused: document.hasFocus(),
      });
    };

    if (gamecastClientRef.current) {
      window.addEventListener('blur', handleBlur);
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (gamecastClientRef.current) {
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      }
    };
  }, [gamecastClientRef.current, currentRoom, externalKeyboardConnected]);

  useEffect(() => {
    if (!gamecastClientRef.current || !currentRoom) {
      return;
    }

    const videoElement = document.getElementById(
      'gamecastVideoTag',
    ) as HTMLVideoElement | null;

    if (!videoElement) {
      return;
    }

    const pressedKeys = new Set<string>();

    const onKeyDown = (e: KeyboardEvent) => {
      pressedKeys.add(e.key);
    };

    const onKeyUp = (e: KeyboardEvent) => {
      pressedKeys.delete(e.key);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    const releaseAllKeys = () => {
      pressedKeys.forEach((key) => {
        videoElement.dispatchEvent(
          new KeyboardEvent('keyup', { key, bubbles: true, cancelable: true }),
        );
      });
      pressedKeys.clear();
    };

    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener('contextmenu', onContextMenu);

    const onBlur = () => {
      releaseAllKeys();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        releaseAllKeys();
      }
    };

    const onPageHide = () => {
      releaseAllKeys();
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [gamecastClientRef.current, currentRoom]);

  return {
    initializeGamecastStream,
    closeGamecastStream,
    abortGamecastConnection,
    handleReconnectGamecast,
    gamecastClientRef,
    processActiveStreamSession,
    initializeGamecastConnectionTimeout,
    syncSdkCursorPos,

    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
  };
};
